// Copyright 2023, Imprivata, Inc.  All rights reserved.
import { isActionOf } from 'typesafe-actions';
import { catchError, filter, from, of, switchMap } from 'rxjs';
import { createSpan } from '@imprivata-cloud/common';
import type { Epic } from 'redux-observable';
import type { Action, EpicDependencies, RootState } from '../types';
import type { ApiError } from '../../api/types';
import { getCredentialsService } from '../../services/getCredentials';
import {
  getCredentialsAction,
  isSamlAuthenticationAction,
  uiCaptureCredentialsAction,
} from '../actions';
import { showWindow } from '../../agent-api';
import { ShowWindowOption } from '../../agent-api/types';
import { debug } from '../../utils/logging';
import { SpanNames } from '../../tracing/constants';

export const getCredentialsEpic: Epic<
  Action,
  Action,
  RootState,
  Partial<EpicDependencies>
> = (action$, state$) => {
  return action$.pipe(
    filter(isActionOf(getCredentialsAction.request)),
    switchMap(({ payload }) => {
      createSpan(SpanNames.GET_USER_APP_CREDENTIALS);

      const { oidcAuthorizationCode } = payload;

      const codingContext = state$.value.appAccessContext?.codingContext;

      return from(
        getCredentialsService({
          oidcAuthorizationCode,
          codingContext,
        }),
      ).pipe(
        switchMap(response => {
          if (typeof response === 'object') {
            void showWindow(ShowWindowOption.show);
            return of(uiCaptureCredentialsAction(response));
          } else {
            return of(
              getCredentialsAction.success(),
              isSamlAuthenticationAction(response),
            );
          }
        }),
        catchError((error: ApiError) => {
          debug('[GetCredentials] error', JSON.stringify({ error }));
          return of(getCredentialsAction.failure());
        }),
      );
    }),
  );
};
